import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatIconModule } from '@angular/material/icon';
import { MemberDashboardService } from '../member-dashboard.service';

@Component({
  selector: 'app-blood-report-history',
  standalone: true,
  imports: [CommonModule, MatPaginatorModule, MatIconModule],
  templateUrl: './blood-report-history.component.html',
  styleUrl: './blood-report-history.component.css'
})
export class BloodReportHistoryComponent implements OnInit, OnChanges {
  @Input() memberId!: string;
  @Output() loadingChange = new EventEmitter<boolean>();

  bloodReportSummaries: any[] = [];
  bloodReportTotalCount = 0;
  bloodReportPageSize = 10;
  bloodReportPageIndex = 0;
  isLoading = false;

  expandedReportIndex: number | null = null;
  smartSectionsMap: { [reportId: string]: any[] } = {};
  loadingSmartSectionsMap: { [reportId: string]: boolean } = {};

  constructor(private dashboard: MemberDashboardService) {}

  ngOnInit(): void {
    if (this.memberId) {
      this.loadBloodReportHistory(0);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['memberId'] && !changes['memberId'].firstChange && this.memberId) {
      this.loadBloodReportHistory(0);
    }
  }

  private loadBloodReportHistory(pageIndex: number): void {
    if (!this.memberId) {
      return;
    }

    this.isLoading = true;
    this.loadingChange.emit(true);
    const pageNo = pageIndex + 1;

    this.dashboard.getMemberBloodReportSummaries(this.memberId, pageNo, this.bloodReportPageSize)
      .subscribe({
        next: (response: any) => {
          const result = response && response.result ? response.result : response;
          this.bloodReportSummaries = result && result.data ? result.data : [];
          this.bloodReportTotalCount = result && typeof result.totalCount === 'number'
            ? result.totalCount
            : (this.bloodReportSummaries ? this.bloodReportSummaries.length : 0);
          this.bloodReportPageIndex = pageIndex;
          this.expandedReportIndex = null;
          this.isLoading = false;
          this.loadingChange.emit(false);
        },
        error: () => {
          this.bloodReportSummaries = [];
          this.bloodReportTotalCount = 0;
          this.isLoading = false;
          this.loadingChange.emit(false);
        }
      });
  }

  onBloodReportPageChange(page: any): void {
    const newIndex = page && typeof page.pageIndex === 'number' ? page.pageIndex : 0;
    this.loadBloodReportHistory(newIndex);
  }

  onBloodReportViewReport(report: any): void {
    if (!report || !report.bloodReportAnalysisUrl) {
      return;
    }
    window.open(report.bloodReportAnalysisUrl, '_blank');
  }

  toggleReportExpansion(index: number, report: any): void {
    const isCurrentlyExpanded = this.expandedReportIndex === index;
    this.expandedReportIndex = isCurrentlyExpanded ? null : index;

    if (!isCurrentlyExpanded && report?.reportId && !this.smartSectionsMap.hasOwnProperty(report.reportId)) {
      this.fetchSmartSections(report.reportId);
    }
  }

  isReportExpanded(index: number): boolean {
    return this.expandedReportIndex === index;
  }

  isLoadingSmartSections(reportId: string): boolean {
    return !!this.loadingSmartSectionsMap[reportId];
  }

  getSmartSections(reportId: string): any[] {
    return this.smartSectionsMap[reportId] || [];
  }

  private fetchSmartSections(reportId: string): void {
    this.loadingSmartSectionsMap[reportId] = true;

    this.dashboard.getMemberBloodReportSmartSections(reportId)
      .subscribe({
        next: (response: any) => {
          const result = response && response.result ? response.result : response;
          const raw = result?.Smart_report_sections ?? result ?? [];
          this.smartSectionsMap[reportId] = Array.isArray(raw) ? raw : [];
          this.loadingSmartSectionsMap[reportId] = false;
        },
        error: () => {
          this.smartSectionsMap[reportId] = [];
          this.loadingSmartSectionsMap[reportId] = false;
        }
      });
  }

  formatUploadedAt(dateString: string): string {
    if (!dateString) {
      return '';
    }
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return '';
    }

    const day = date.getDate().toString().padStart(2, '0');
    const monthNames = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();

    let hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    if (hours === 0) {
      hours = 12;
    }

    const hourString = hours.toString();
    return `${day} ${month}, ${year} , ${hourString}:${minutes} ${ampm}`;
  }
}
